export type TemplateType = 'clean' | 'bold' | 'creative' | 'tech' | 'executive' | 'modern'

export interface ColorPalette {
  id: string
  name: string
  colors: string[] // Preview swatches (2-3 colors)
}

// Clean Template Palettes
export interface CleanColors {
  primary: string      // Title, links, accents
  text: string         // Main text
  textMuted: string    // Secondary text
  border: string       // Borders and lines
}

export const cleanPalettes: Record<string, CleanColors> = {
  blue: {
    primary: '#1e40af',    // Blue
    text: '#0f172a',       // Slate 900
    textMuted: '#64748b',  // Slate 500
    border: '#e2e8f0',     // Slate 200
  },
  green: {
    primary: '#047857',    // Emerald 700
    text: '#0f172a',
    textMuted: '#64748b',
    border: '#e2e8f0',
  },
  burgundy: {
    primary: '#9f1239',    // Rose 800
    text: '#1c1917',       // Stone 900
    textMuted: '#57534e',  // Stone 600
    border: '#e7e5e4',     // Stone 200
  },
}

export const cleanPaletteOptions: ColorPalette[] = [
  { id: 'blue', name: 'Azul', colors: ['#1e40af', '#0f172a'] },
  { id: 'green', name: 'Verde', colors: ['#047857', '#0f172a'] },
  { id: 'burgundy', name: 'Burdeos', colors: ['#9f1239', '#1c1917'] },
]

// Bold Template Palettes
export interface BoldColors {
  primary: string      // Accent color (coral, teal, etc)
  secondary: string    // Secondary accent (indigo, etc)
  dark: string         // Sidebar background
  sidebarText: string  // Text on dark sidebar
  sidebarMuted: string // Muted text on sidebar
}

export const boldPalettes: Record<string, BoldColors> = {
  coral: {
    primary: '#FF6B6B',    // Coral
    secondary: '#4F46E5',  // Indigo
    dark: '#1F1F1F',
    sidebarText: '#FFFFFF',
    sidebarMuted: '#D1D5DB',
  },
  teal: {
    primary: '#14B8A6',    // Teal
    secondary: '#F97316',  // Orange
    dark: '#134E4A',       // Teal 900
    sidebarText: '#FFFFFF',
    sidebarMuted: '#99F6E4',
  },
  purple: {
    primary: '#A855F7',    // Purple
    secondary: '#EAB308',  // Yellow
    dark: '#3B0764',       // Purple 950
    sidebarText: '#FFFFFF',
    sidebarMuted: '#E9D5FF',
  },
}

export const boldPaletteOptions: ColorPalette[] = [
  { id: 'coral', name: 'Coral', colors: ['#FF6B6B', '#4F46E5', '#1F1F1F'] },
  { id: 'teal', name: 'Teal', colors: ['#14B8A6', '#F97316', '#134E4A'] },
  { id: 'purple', name: 'Purpura', colors: ['#A855F7', '#EAB308', '#3B0764'] },
]

// Creative Template Palettes
export interface CreativeColors {
  accent1: string      // First gradient color
  accent2: string      // Second gradient color
  accent3: string      // Third gradient color
  accent1Light: string // Light version of accent1
  accent2Light: string // Light version of accent2
  accent3Light: string // Light version of accent3
}

export const creativePalettes: Record<string, CreativeColors> = {
  indigo: {
    accent1: '#6366f1',      // Indigo
    accent2: '#a855f7',      // Purple
    accent3: '#ec4899',      // Pink
    accent1Light: '#e0e7ff', // Indigo light
    accent2Light: '#f3e8ff', // Purple light
    accent3Light: '#fce7f3', // Pink light
  },
  ocean: {
    accent1: '#0891b2',      // Cyan
    accent2: '#0d9488',      // Teal
    accent3: '#10b981',      // Emerald
    accent1Light: '#cffafe', // Cyan light
    accent2Light: '#ccfbf1', // Teal light
    accent3Light: '#d1fae5', // Emerald light
  },
  sunset: {
    accent1: '#f97316',      // Orange
    accent2: '#ef4444',      // Red
    accent3: '#ec4899',      // Pink
    accent1Light: '#ffedd5', // Orange light
    accent2Light: '#fee2e2', // Red light
    accent3Light: '#fce7f3', // Pink light
  },
}

export const creativePaletteOptions: ColorPalette[] = [
  { id: 'indigo', name: 'Indigo', colors: ['#6366f1', '#a855f7', '#ec4899'] },
  { id: 'ocean', name: 'Ocean', colors: ['#0891b2', '#0d9488', '#10b981'] },
  { id: 'sunset', name: 'Atardecer', colors: ['#f97316', '#ef4444', '#ec4899'] },
]

// Tech Template Palettes (Code Editor style)
export interface TechColors {
  background: string   // Page background
  gutter: string       // Line numbers area background
  text: string         // Main text
  textMuted: string    // Secondary text
  border: string       // Borders and lines
  accent: string       // Primary accent (links, highlights)
  comment: string      // Section titles (like code comments)
  keyword: string      // Keywords (const, function, etc)
  function: string     // Function names
  string: string       // String values
  number: string       // Numbers/dates
  tagBg: string        // Background for skill tags
}

export const techPalettes: Record<string, TechColors> = {
  dark: {
    background: '#1E1E1E',    // VS Code dark
    gutter: '#252526',        // Slightly lighter
    text: '#D4D4D4',          // Light gray
    textMuted: '#9CDCFE',     // Light blue for readability
    border: '#3C3C3C',        // Subtle border
    accent: '#569CD6',        // Blue accent
    comment: '#6A9955',       // Green comments
    keyword: '#C586C0',       // Purple keywords
    function: '#DCDCAA',      // Yellow functions
    string: '#CE9178',        // Orange strings
    number: '#B5CEA8',        // Light green numbers
    tagBg: '#2D2D2D',         // Tag background
  },
  matrix: {
    background: '#0D0D0D',    // Almost black
    gutter: '#0A0A0A',
    text: '#00FF41',          // Matrix green
    textMuted: '#00CC33',
    border: '#003300',
    accent: '#00FF41',
    comment: '#006600',
    keyword: '#00FF41',
    function: '#39FF14',
    string: '#00FF41',
    number: '#32CD32',
    tagBg: '#001100',
  },
  light: {
    background: '#FFFFFF',    // White
    gutter: '#F6F8FA',        // GitHub light gray
    text: '#24292F',          // Dark text
    textMuted: '#57606A',     // Muted text
    border: '#D0D7DE',        // Light border
    accent: '#0969DA',        // Blue links
    comment: '#6E7781',       // Gray comments
    keyword: '#CF222E',       // Red keywords
    function: '#8250DF',      // Purple functions
    string: '#0A3069',        // Dark blue strings
    number: '#0550AE',        // Blue numbers
    tagBg: '#F6F8FA',         // Light tag bg
  },
}

export const techPaletteOptions: ColorPalette[] = [
  { id: 'dark', name: 'VS Code', colors: ['#1E1E1E', '#569CD6', '#DCDCAA'] },
  { id: 'matrix', name: 'Matrix', colors: ['#0D0D0D', '#00FF41', '#003300'] },
  { id: 'light', name: 'GitHub', colors: ['#FFFFFF', '#0969DA', '#8250DF'] },
]

// Executive Template Palettes (Elegant/Formal style)
export interface ExecutiveColors {
  background: string   // Page background
  primary: string      // Name, section titles
  accent: string       // Lines, highlights, company names
  text: string         // Main text
  textMuted: string    // Secondary text, dates
}

export const executivePalettes: Record<string, ExecutiveColors> = {
  navy: {
    background: '#FFFEF9',    // Warm white/cream
    primary: '#1e3a5f',       // Deep navy
    accent: '#b8860b',        // Dark goldenrod
    text: '#2c3e50',          // Dark slate
    textMuted: '#5d6d7e',     // Slate gray
  },
  burgundy: {
    background: '#FFFDF8',    // Warm cream
    primary: '#722f37',       // Burgundy
    accent: '#c9a227',        // Gold
    text: '#3c2415',          // Dark brown
    textMuted: '#6b5344',     // Brown gray
  },
  charcoal: {
    background: '#FAFAFA',    // Cool white
    primary: '#36454f',       // Charcoal
    accent: '#708090',        // Slate gray (silver tone)
    text: '#2f3640',          // Dark charcoal
    textMuted: '#636e72',     // Gray
  },
}

export const executivePaletteOptions: ColorPalette[] = [
  { id: 'navy', name: 'Navy/Oro', colors: ['#1e3a5f', '#b8860b', '#FFFEF9'] },
  { id: 'burgundy', name: 'Burdeos', colors: ['#722f37', '#c9a227', '#FFFDF8'] },
  { id: 'charcoal', name: 'Carbón', colors: ['#36454f', '#708090', '#FAFAFA'] },
]

// Modern Template Palettes (Startup/Tech style)
export interface ModernColors {
  primary: string     // Main accent color
  dark: string        // Dark version for emphasis
  light: string       // Light background tint
  text: string        // Main text color
  textMuted: string   // Secondary text color
}

export const modernPalettes: Record<string, ModernColors> = {
  gold: {
    primary: '#F5A623',    // Gold/Amber
    dark: '#3D3D3D',       // Dark gray
    light: '#F5F5F5',      // Light gray
    text: '#3D3D3D',       // Dark gray
    textMuted: '#666666',  // Medium gray
  },
  indigo: {
    primary: '#6366F1',    // Indigo 500
    dark: '#312E81',       // Indigo 900
    light: '#E0E7FF',      // Indigo 100
    text: '#1F2937',       // Gray 800
    textMuted: '#6B7280',  // Gray 500
  },
  emerald: {
    primary: '#10B981',    // Emerald 500
    dark: '#064E3B',       // Emerald 900
    light: '#D1FAE5',      // Emerald 100
    text: '#1F2937',
    textMuted: '#6B7280',
  },
  rose: {
    primary: '#F43F5E',    // Rose 500
    dark: '#881337',       // Rose 900
    light: '#FFE4E6',      // Rose 100
    text: '#1F2937',
    textMuted: '#6B7280',
  },
}

export const modernPaletteOptions: ColorPalette[] = [
  { id: 'gold', name: 'Dorado', colors: ['#F5A623', '#3D3D3D', '#F5F5F5'] },
  { id: 'indigo', name: 'Indigo', colors: ['#6366F1', '#312E81', '#E0E7FF'] },
  { id: 'emerald', name: 'Emerald', colors: ['#10B981', '#064E3B', '#D1FAE5'] },
  { id: 'rose', name: 'Rose', colors: ['#F43F5E', '#881337', '#FFE4E6'] },
]

// Helper to get palette options by template
export function getPaletteOptions(template: TemplateType): ColorPalette[] {
  switch (template) {
    case 'clean':
      return cleanPaletteOptions
    case 'bold':
      return boldPaletteOptions
    case 'creative':
      return creativePaletteOptions
    case 'tech':
      return techPaletteOptions
    case 'executive':
      return executivePaletteOptions
    case 'modern':
      return modernPaletteOptions
    default:
      return cleanPaletteOptions
  }
}

// Helper to get default palette for a template
export function getDefaultPalette(template: TemplateType): string {
  switch (template) {
    case 'clean':
      return 'blue'
    case 'bold':
      return 'coral'
    case 'creative':
      return 'indigo'
    case 'tech':
      return 'dark'
    case 'executive':
      return 'navy'
    case 'modern':
      return 'gold'
    default:
      return 'blue'
  }
}
